import { Controller, useForm } from 'react-hook-form'
import TextFieldForm from '../../ui/TextFieldForm'
import { Checkbox, FormControlLabel, Grid, styled } from '@mui/material'
import { yupResolver } from '@hookform/resolvers/yup'
import CustomTypography from '../../ui/CustomTypography'
import { useTheme } from '@mui/system'
import React, {
	Dispatch,
	Fragment,
	SetStateAction,
	useCallback,
	useEffect,
	useMemo,
} from 'react'
import { isEmpty } from 'lodash'
import {
	AmlInspectionStatus,
	CompanyFormType,
	ReceiveCompanyPaymentMethod,
} from '../../../types/companiesTypes'
import SelectFieldForm from '../../ui/SelectFieldForm'
import { useAppDispatch, useAppSelector } from '../../../hooks/redux'
import { companiesYupSchemes } from '../../../helpers/resolverScheme/companiesSchemes'
import { CustomButton } from '../../ui/CustomButton'
import { companiesActionCreators } from '../../../store/actionCreators/companiesActionCreators'
import {
	labelForConnectedPersons,
	QTY_PERSONS,
	receiveCompanyPaymentMethod,
} from './utils'

const bankOptions = [
	{
		label: 'CBZ',
		value: 1,
	},
]

const countryOptions = [
	{
		label: 'Zimbabwe',
		value: 240,
	},
]

const StyledCompanyForm = styled('form')(() => ({
	'.currency-title': {
		// paddingLeft: '10px',
		marginTop: '10px',
		marginBottom: '10px',
		'&:first-of-type': {
			marginTop: 0,
		},
	},
	'.t-right': {
		'.MuiFormControl-root': {
			marginBottom: 0,
			'.MuiInputBase-input': {
				textAlign: 'right',
			},
		},
	},
	'.MuiTypography-root': {
		lineHeight: '36px',
	},
}))

type CompanyFormProps = {
	setIsDisabledBtn: Dispatch<SetStateAction<boolean>>
	variant: 'add-company' | 'edit-company'
	onAmlCheckingBtn: () => void
	onSave: (dataForm: CompanyFormType, companyId?: string | number) => void
	setIsValidNumberField: Dispatch<SetStateAction<boolean>>
}

const CompanyForm = ({
	setIsDisabledBtn,
	onSave,
	variant,
	onAmlCheckingBtn,
	setIsValidNumberField,
}: CompanyFormProps) => {
	const theme = useTheme()
	const dispatch = useAppDispatch()

	const { companyDetails, accNumberData, accNumberAfterValidation } = useAppSelector(
		state => state.companies,
	)

	const isShownAmlCheckingBtn = useMemo(
		() =>
			variant === 'edit-company' &&
			(companyDetails?.amlInspectionStatus === AmlInspectionStatus.Initiated ||
				companyDetails?.amlInspectionStatus === AmlInspectionStatus.Pending ||
				companyDetails?.amlInspectionStatus === AmlInspectionStatus.RejectedWithRetry),
		[companyDetails?.amlInspectionStatus, variant],
	)

	const {
		handleSubmit,
		formState,
		control,
		setValue,
		watch,
		setError,
		clearErrors,
		trigger,
	} = useForm<CompanyFormType>({
		resolver: yupResolver(companiesYupSchemes.companyForm),
	})

	const watchedAccountNumber = watch('accountNumber')
	const watchedReceiveCompanyPaymentMethod = watch('receivePaymentMethod')

	const isValidAccNumberField = useMemo(() => {
		const currentAccNumber = watchedAccountNumber ? watchedAccountNumber.trim() : ''
		const isValidAccNumberAfter = accNumberData ? accNumberData.isValid : false

		return (
			(accNumberAfterValidation === currentAccNumber && isValidAccNumberAfter) ||
			watchedReceiveCompanyPaymentMethod === ReceiveCompanyPaymentMethod.Cash
		)
	}, [
		accNumberAfterValidation,
		accNumberData,
		watchedAccountNumber,
		watchedReceiveCompanyPaymentMethod,
	])

	useEffect(() => {
		setIsValidNumberField(isValidAccNumberField)
	}, [isValidAccNumberField, setIsValidNumberField])

	const setFormValue = useCallback(() => {
		setValue('bankId', 1)
		setValue('countryId', 240)

		Array.from({ length: QTY_PERSONS }, (_, i) => i).forEach((_, index) => {
			setValue(
				`connectedPersons.${index}.firstName`,
				companyDetails?.connectedPersons
					? companyDetails?.connectedPersons[index]?.firstName
					: '',
			)
			setValue(
				`connectedPersons.${index}.lastName`,
				companyDetails?.connectedPersons
					? companyDetails?.connectedPersons[index]?.lastName
					: '',
			)
			setValue(
				`connectedPersons.${index}.idNumber`,
				companyDetails?.connectedPersons
					? companyDetails?.connectedPersons[index]?.idNumber
					: '',
			)
		})

		if (variant === 'add-company') {
			setValue('receivePaymentMethod', ReceiveCompanyPaymentMethod.Bank)
			setValue('accountNumber', '')
		}

		if (variant === 'edit-company' && companyDetails) {
			setValue('companyName', companyDetails.companyName)
			setValue('accountNumber', companyDetails.accountNumber || '')
			setValue('isActive', companyDetails.isActive)
			setValue('notificationEmail', companyDetails.notificationEmail)
			setValue('receivePaymentMethod', companyDetails.receivePaymentMethod)
		}
	}, [companyDetails, setValue, variant])

	useEffect(() => {
		setFormValue()
	}, [setFormValue])

	useEffect(() => {
		if (watchedReceiveCompanyPaymentMethod === ReceiveCompanyPaymentMethod.Cash) {
			trigger('accountNumber')
		}
	}, [trigger, watchedReceiveCompanyPaymentMethod])

	useEffect(() => {
		if (typeof accNumberData?.isValid === 'boolean') {
			if (accNumberData?.isValid) {
				return clearErrors('accountNumber')
			} else {
				return setError('accountNumber', { message: accNumberData?.errorDescription })
			}
		}
	}, [
		accNumberData,
		accNumberData?.errorDescription,
		accNumberData?.isValid,
		clearErrors,
		setError,
	])

	useEffect(() => {
		if (isEmpty(formState.errors)) {
			setIsDisabledBtn(false)
		} else {
			setIsDisabledBtn(true)
		}
	}, [formState, setIsDisabledBtn])

	const onSubmit = (dataForm: CompanyFormType) => {
		const filteredConnectedPersons = dataForm.connectedPersons
			? dataForm.connectedPersons.filter(obj =>
					Object.values(obj).some(
						value => value !== '' && value !== null && value !== undefined,
					),
			  )
			: []

		if (filteredConnectedPersons.length === 0) {
			delete dataForm.connectedPersons
		}

		if (!isValidAccNumberField) {
			const requestData = {
				accountNumber: dataForm.accountNumber ? dataForm.accountNumber.trim() : '',
				bankId: dataForm.bankId,
			}
			dispatch(companiesActionCreators.validateCompanyAccNumber(requestData))

			return
		}

		companyDetails ? onSave(dataForm, companyDetails.id) : onSave(dataForm)
	}

	return (
		<StyledCompanyForm id="company-form" onSubmit={handleSubmit(onSubmit)}>
			<Grid container>
				<Grid item md={5} xs={12}>
					<CustomTypography colorText={theme.palette.warning.dark}>
						Name:
					</CustomTypography>
				</Grid>
				<Grid item md={7} xs={12}>
					<div className="t-right">
						<TextFieldForm<CompanyFormType>
							name="companyName"
							control={control}
							type="text"
							inputMode="text"
							autoComplete="off"
							maxLength={64}
							disabled={variant === 'edit-company'}
						/>
					</div>
				</Grid>

				<Grid item md={5} xs={12}>
					<CustomTypography
						colorText={theme.palette.warning.dark}
						className="flex align-items-end h-100"
					>
						Country:
					</CustomTypography>
				</Grid>
				<Grid item md={7} xs={12}>
					<div className="t-right">
						<SelectFieldForm<CompanyFormType>
							name="countryId"
							control={control}
							label=""
							options={countryOptions}
							className="mt-0"
							disabled
						/>
					</div>
				</Grid>
				<Grid item md={5} xs={12}>
					<CustomTypography colorText={theme.palette.warning.dark}>
						Notification Email:
					</CustomTypography>
				</Grid>
				<Grid item md={7} xs={12}>
					<div className="t-right">
						<TextFieldForm<CompanyFormType>
							name="notificationEmail"
							control={control}
							type="text"
							inputMode="text"
							autoComplete="off"
							maxLength={64}
						/>
					</div>
				</Grid>

				<Grid item md={5} xs={12} sx={{ marginTop: { xs: 4, md: 3 } }}>
					<CustomTypography
						colorText={theme.palette.warning.dark}
						className="flex align-items-end h-100"
					>
						Receive Payment Method:
					</CustomTypography>
				</Grid>
				<Grid item md={7} xs={12} sx={{ marginTop: { xs: 0, md: 3 } }}>
					<div className="t-right">
						<SelectFieldForm<CompanyFormType>
							name="receivePaymentMethod"
							control={control}
							label=""
							options={receiveCompanyPaymentMethod}
							className="mt-0"
							disabled={variant === 'edit-company'}
						/>
					</div>
				</Grid>

				{watchedReceiveCompanyPaymentMethod === ReceiveCompanyPaymentMethod.Bank ? (
					<>
						<Grid item md={5} xs={12} marginTop={1}>
							<CustomTypography
								colorText={theme.palette.warning.dark}
								className="flex align-items-end h-100"
							>
								Bank Name:
							</CustomTypography>
						</Grid>
						<Grid item md={7} xs={12} sx={{ marginTop: { xs: 0, md: 1 } }}>
							<div className="t-right">
								<SelectFieldForm<CompanyFormType>
									name="bankId"
									control={control}
									label=""
									options={bankOptions}
									className="mt-0"
									disabled
								/>
							</div>
						</Grid>
						<Grid item md={5} xs={12}>
							<CustomTypography colorText={theme.palette.warning.dark}>
								Account Number:
							</CustomTypography>
						</Grid>
						<Grid item md={7} xs={12}>
							<div className="t-right">
								<TextFieldForm<CompanyFormType>
									name="accountNumber"
									control={control}
									type="text"
									inputMode="text"
									autoComplete="off"
									maxLength={32}
								/>
							</div>
						</Grid>
					</>
				) : (
					Array.from({ length: QTY_PERSONS }, (_, i) => i).map((_, index) => (
						<Fragment key={index}>
							<Grid item xs={12} paddingLeft={2} marginTop={1}>
								<CustomTypography
									colorText={theme.palette.warning.dark}
									fontStyle="italic"
								>
									{labelForConnectedPersons[index]}
								</CustomTypography>
							</Grid>
							<Grid item md={5} xs={12}>
								<CustomTypography colorText={theme.palette.warning.dark}>
									First Name:
								</CustomTypography>
							</Grid>
							<Grid item md={7} xs={12}>
								<div className="t-right">
									<TextFieldForm<CompanyFormType>
										name={`connectedPersons.${index}.firstName`}
										control={control}
										type="text"
										inputMode="text"
										autoComplete="off"
										maxLength={64}
									/>
								</div>
							</Grid>
							<Grid item md={5} xs={12}>
								<CustomTypography colorText={theme.palette.warning.dark}>
									Last Name:
								</CustomTypography>
							</Grid>
							<Grid item md={7} xs={12}>
								<div className="t-right">
									<TextFieldForm<CompanyFormType>
										name={`connectedPersons.${index}.lastName`}
										control={control}
										type="text"
										inputMode="text"
										autoComplete="off"
										maxLength={64}
									/>
								</div>
							</Grid>
							<Grid item md={5} xs={12}>
								<CustomTypography colorText={theme.palette.warning.dark}>
									ID number:
								</CustomTypography>
							</Grid>
							<Grid item md={7} xs={12}>
								<div className="t-right">
									<TextFieldForm<CompanyFormType>
										name={`connectedPersons.${index}.idNumber`}
										control={control}
										type="text"
										inputMode="text"
										autoComplete="off"
										maxLength={64}
									/>
								</div>
							</Grid>
						</Fragment>
					))
				)}

				{isShownAmlCheckingBtn && (
					<Grid
						item
						md={12}
						// xs={isShownAmlCheckingBtn ? 12 : 0}
						alignItems="center"
						mt={2}
					>
						<CustomButton type="button" onClick={onAmlCheckingBtn}>
							Pass Aml Checking
						</CustomButton>
					</Grid>
				)}
				<Grid item md={5} xs={12} alignSelf="center" mt={2}>
					<CustomTypography colorText={theme.palette.warning.dark}>
						Status:
					</CustomTypography>
				</Grid>
				<Grid item md={7} xs={12} mt={2}>
					<Controller
						name="isActive"
						control={control}
						render={({ field: { onChange, value } }) => (
							<FormControlLabel
								sx={{ display: 'flex', justifyContent: 'end' }}
								control={
									<Checkbox
										onChange={event => {
											onChange(event.target.checked)
										}}
										checked={typeof value === 'undefined' ? false : value}
									/>
								}
								label="Activate"
							/>
						)}
					/>
				</Grid>
			</Grid>
		</StyledCompanyForm>
	)
}

export default CompanyForm
